/**
 * lib/diploksClient.js
 * --------------------------------------------------------------------------
 * Thin client that wraps the central Diploks deduction engine for frontend
 * callers.  Every premium feature should consume Diploks through this
 * helper — never re-implement the consent/balance check inline.
 *
 * Flow:
 *   1. Ensure consent (open the Activate Diploks Usage modal on demand).
 *   2. POST /api/diploks/feature/consume with the feature_key.
 *   3. On 200 → fire `diploks:wallet-changed` event + show a non-blocking
 *      toast + return the consume result so the caller can run the
 *      feature.  Caller is responsible for calling `reverse(consumeId)`
 *      if its own feature throws after the deduction.
 *   4. On 402 (insufficient) → resolve with `lowBalance` so the caller
 *      can show the LowBalanceDialog.
 *   5. On 409 (consent) → throw `CONSENT_REQUIRED`; the consent gate
 *      should have caught it but we still surface the error.
 *
 * Idempotency: callers SHOULD pass a stable `idempotencyKey` per
 * feature-instance (e.g. `paper-review:<paper_id>`) so retries do not
 * double-charge.
 */
import api from "./api";
import { toast } from "sonner";

export class DiploksError extends Error {
  constructor(code, payload) {
    super(payload?.reason || code);
    this.code = code;
    this.payload = payload;
  }
}

export async function getConsentStatus() {
  const r = await api.get("/diploks/usage-consent/status");
  return r.data;
}

export async function acceptConsent({ scope = "personal" } = {}) {
  const r = await api.post("/diploks/usage-consent/accept", {
    scope, accept_checkbox: true,
  });
  return r.data;
}

/**
 * @param {object} args
 * @param {string} args.featureKey
 * @param {string} [args.idempotencyKey]
 * @param {Record<string, any>} [args.metadata]
 * @returns {Promise<{ok:true,result:object} | {ok:false,code:string,payload:object}>}
 */
export async function consume({ featureKey, idempotencyKey, metadata }) {
  try {
    const r = await api.post("/diploks/feature/consume", {
      feature_key: featureKey,
      idempotency_key: idempotencyKey,
      metadata,
    });
    window.dispatchEvent(new CustomEvent("diploks:wallet-changed"));
    const data = r.data;
    if (!data.duplicate) {
      toast.success(`${data.cost} Diploks used.`, {
        description: `Balance: ${data.balance_after}`,
      });
    }
    return { ok: true, result: data };
  } catch (e) {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail || e?.response?.data || {};
    const code = (typeof detail === "object" && detail.error_code) ||
                 (status === 402 ? "insufficient_balance"
                  : status === 409 ? "consent_required"
                  : "consume_failed");
    return { ok: false, code, payload: detail, status };
  }
}

export async function reverse(consumeId, reason = "feature_failed") {
  try {
    await api.post("/diploks/feature/reverse",
      { consume_id: consumeId, reason });
    window.dispatchEvent(new CustomEvent("diploks:wallet-changed"));
    toast.message("Diploks restored because the action could not be completed.");
    return true;
  } catch {
    return false;
  }
}

export async function precheck({ featureKey, amount }) {
  const r = await api.post("/diploks/feature/check", {
    feature_key: featureKey,
    amount,
  });
  return r.data;
}
